import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent,
} from 'react'
import {
  boardMeasures,
  builderCatalog,
  dropCustomCard,
  makeCustomCard,
  saveBoard,
  type BoardMeasures,
  type BuilderCard,
  type DashboardCard,
} from '../api/dashboard'
import { toast } from '../ui/notify'
import { placeLinks } from './cardLinks'

/**
 * Constructorul de tablouri - `public/js/dashboard/builder.js`, fara gridstack.
 *
 * -----------------------------------------------------------------------------
 * DE CE FARA GRIDSTACK
 * -----------------------------------------------------------------------------
 *
 * Portalul folosea gridstack 0.3, care sta pe jQuery UI si pe lodash. v4 nu are
 * jQuery, si "gridstack nu se incarca deloc" e chiar ce scria in `DashboardCard`
 * pana la livrarea asta: modul de editare se desena si nu facea nimic. Grila
 * de patru coloane pe trei randuri incape in cateva zeci de randuri de
 * aritmetica - locul (coloana, randul) si marimea (celule) sunt ale omului,
 * iar tot ce facea biblioteca e sa le socoteasca si sa le scrie in pixeli.
 * Exact asta facea si `builder.js` dupa gridstack ("GEOMETRIA O SCRIEM NOI, IN
 * PIXELI"), fiindca foile plugin-ului nu s-au aplicat pe CEC.
 *
 * Markupul e cel din `index.php` in constructor: `.odb-slot.grid-stack-item`
 * cu `data-gs-*`, `.grid-stack-item-content`, uneltele (`.odb-slot-tools`),
 * catalogul (`#odbCatalog`) si formularul "fa-ti o caseta" (`#odbMaker`).
 * Foaia le stie deja.
 *
 * -----------------------------------------------------------------------------
 * CE SE INTAMPLA CU MUTAREA
 * -----------------------------------------------------------------------------
 *
 * Nimic, pana la "Save layout". Se muta in pagina, si atat: cine se razgandeste
 * apasa Done si nu s-a schimbat nimic. Un constructor care salveaza la fiecare
 * miscare face din orice atingere gresita o modificare permanenta.
 *
 * Cardul RAMANE unde l-ai lasat (gridstack `float: true`): pe o grila de 4 x 3
 * un gol e o hotarare, nu o greseala. Ce se calca peste cardul mutat e impins
 * in jos, nimic altceva nu se misca.
 */

export const COLUMNS = 4

export interface BuilderItem {
  id: string
  title: string
  size: string
  w: number
  h: number
  x: number
  y: number
  html: string
  /** Adaugat din catalog si inca nesalvat: are numele, n-are cifra. */
  fresh: boolean
}

const SIZES: Record<string, { w: number; h: number; label: string }> = {
  '1x1': { w: 1, h: 1, label: '1 x 1' },
  '2x1': { w: 2, h: 1, label: '2 x 1' },
  '1x2': { w: 1, h: 2, label: '1 x 2' },
  '2x2': { w: 2, h: 2, label: '2 x 2' },
}

/* ------------------------------------------------------------------------ *
 * ARITMETICA GRILEI - pura, ca sa se poata proba fara browser
 * ------------------------------------------------------------------------ */

export function collides(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
}

/** Primul loc liber, rand cu rand, de la stanga - `autoPosition` din gridstack. */
export function firstFree(items: BuilderItem[], w: number, h: number): { x: number; y: number } {
  for (let y = 0; y < 1000; y++) {
    for (let x = 0; x + w <= COLUMNS; x++) {
      const spot = { x, y, w, h }
      if (!items.some((it) => collides(it, spot))) return { x, y }
    }
  }
  return { x: 0, y: 0 }
}

/**
 * Asaza cardurile dupa o mutare: cel mutat sta unde l-a lasat omul, iar ce
 * se calca peste el coboara, rand cu rand, pana nu se mai calca peste nimic.
 * Ordinea de asezare e cea de citire (sus-jos, stanga-dreapta), ca ce era
 * deasupra sa ramana deasupra.
 */
export function settle(items: BuilderItem[], fixedId: string | null): BuilderItem[] {
  const fixed = items.filter((it) => it.id === fixedId)
  const rest = items.filter((it) => it.id !== fixedId).sort((a, b) => a.y - b.y || a.x - b.x)
  const placed: BuilderItem[] = fixed.map((it) => ({ ...it }))

  for (const item of rest) {
    const next = { ...item }
    let guard = 0
    while (placed.some((p) => collides(p, next)) && guard++ < 1000) next.y += 1
    placed.push(next)
  }

  /* Ordinea de pe ecran, ca la salvare. */
  return placed.sort((a, b) => a.y - b.y || a.x - b.x)
}

/** Cardurile de pe tablou, cu loc pentru fiecare: cele fara loc intra "unde incape". */
export function layout(cards: DashboardCard[]): BuilderItem[] {
  const items: BuilderItem[] = []
  const later: DashboardCard[] = []
  for (const card of cards) {
    if (card.x != null && card.y != null) {
      items.push({
        id: card.id,
        title: card.title,
        size: card.size,
        w: card.w,
        h: card.h,
        x: card.x,
        y: card.y,
        html: card.html,
        fresh: false,
      })
    } else {
      later.push(card)
    }
  }
  for (const card of later) {
    const spot = firstFree(items, card.w, card.h)
    items.push({
      id: card.id,
      title: card.title,
      size: card.size,
      w: card.w,
      h: card.h,
      ...spot,
      html: card.html,
      fresh: false,
    })
  }
  return settle(items, null)
}

/** Ce pleaca la `saveBoard`: ordinea de citire e cea de pe ecran. */
export function savePayload(items: BuilderItem[]): { card_id: string; size: string; x: number; y: number }[] {
  return [...items].sort((a, b) => a.y - b.y || a.x - b.x).map((it) => ({ card_id: it.id, size: it.size, x: it.x, y: it.y }))
}

/**
 * Inaltimea unei celule, socotita EXACT ca in foaie (`--odb-cell`): trei
 * randuri in ce ramane din inaltimea ferestrei dupa antet si subsol, cu
 * podeaua de 200. Se repeta formula, ca ce vezi cand tragi sa fie ce vezi
 * dupa salvare.
 */
function cellHeight(grid: HTMLElement | null): number {
  const cs = grid ? getComputedStyle(grid) : null
  const gap = parseFloat(cs?.getPropertyValue('--odb-gap') ?? '') || 18
  const chrome = parseFloat(cs?.getPropertyValue('--odb-chrome') ?? '') || 240
  return Math.max(200, Math.round((window.innerHeight - chrome - 2 * gap) / 3))
}

/* ------------------------------------------------------------------------ *
 * ECRANUL
 * ------------------------------------------------------------------------ */

export function BoardBuilder({
  boardId,
  cards,
  placed,
}: {
  boardId: number
  cards: DashboardCard[]
  /** Tabelele au coloanele x/y - altfel se salveaza doar ordinea si marimile. */
  placed: boolean
}) {
  const [items, setItems] = useState<BuilderItem[]>(() => layout(cards))
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [catalog, setCatalog] = useState<BuilderCard[] | null>(null)
  const [catalogFailed, setCatalogFailed] = useState(false)
  const [drag, setDrag] = useState<{ id: string; x: number; y: number } | null>(null)
  const [ask, setAsk] = useState<{
    title: string
    text: string
    ok: string
    then: () => void
  } | null>(null)
  const grid = useRef<HTMLDivElement | null>(null)
  const [geometry, setGeometry] = useState({ colWidth: 0, cell: 200, gap: 18 })

  /* Geometria, in pixeli, din singurul loc care stie adevarul: grila. */
  useLayoutEffect(() => {
    function measure() {
      const el = grid.current
      const cs = el ? getComputedStyle(el) : null
      const gap = parseFloat(cs?.getPropertyValue('--odb-gap') ?? '') || 18
      setGeometry({
        colWidth: (el?.clientWidth ?? 0) / COLUMNS,
        cell: cellHeight(el),
        gap,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  /* Catalogul se umple la deschidere - cine nu construieste nimic n-are de ce
     sa plateasca treizeci si trei de randuri de catalog. Aici constructorul E
     deschis. */
  useEffect(() => {
    let alive = true
    builderCatalog()
      .then((out) => alive && setCatalog(out.catalog))
      .catch(() => alive && setCatalogFailed(true))
    return () => {
      alive = false
    }
  }, [])

  /* Ce s-a mutat si nu s-a salvat nu pleaca in tacere. */
  useEffect(() => {
    if (!dirty) return
    function warn(ev: BeforeUnloadEvent) {
      ev.preventDefault()
      ev.returnValue = 'The layout has changes that are not saved yet.'
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  function touch(next: BuilderItem[]) {
    setItems(next)
    setDirty(true)
  }

  function moveTo(id: string, x: number, y: number) {
    const item = items.find((it) => it.id === id)
    if (!item) return
    const nx = Math.max(0, Math.min(COLUMNS - item.w, x))
    const ny = Math.max(0, y)
    if (nx === item.x && ny === item.y) return
    touch(
      settle(
        items.map((it) => (it.id === id ? { ...it, x: nx, y: ny } : it)),
        id,
      ),
    )
  }

  function resize(id: string, size: string) {
    const dims = SIZES[size]
    if (!dims) return
    touch(
      settle(
        items.map((it) =>
          it.id === id
            ? {
                ...it,
                size,
                w: dims.w,
                h: dims.h,
                x: Math.min(it.x, COLUMNS - dims.w),
              }
            : it,
        ),
        id,
      ),
    )
  }

  function drop(id: string) {
    touch(items.filter((it) => it.id !== id))
  }

  /*
   * Cardul adaugat intra ca un loc GOL, cu numele lui. Nu se cere serverului
   * sa deseneze cardul pe loc: cifra lui se citeste corect abia dupa salvare,
   * cand pagina se incarca din nou. Locul gol spune limpede ce urmeaza sa fie
   * acolo.
   */
  function add(card: BuilderCard) {
    const dims = SIZES[card.size] ?? SIZES['1x1']
    const spot = firstFree(items, dims.w, dims.h)
    touch([
      ...items,
      {
        id: card.id,
        title: card.title,
        size: card.size in SIZES ? card.size : '1x1',
        w: dims.w,
        h: dims.h,
        ...spot,
        html: '',
        fresh: true,
      },
    ])
  }

  /* ---- mutarea cu mausul: manerul se apuca, cardul sare din celula in celula ---- */
  const start = useRef<{
    id: string
    px: number
    py: number
    x: number
    y: number
  } | null>(null)

  function onGripDown(ev: PointerEvent<HTMLSpanElement>, item: BuilderItem) {
    if (ev.button !== 0) return
    ev.preventDefault()
    ;(ev.currentTarget as HTMLElement).setPointerCapture?.(ev.pointerId)
    start.current = {
      id: item.id,
      px: ev.clientX,
      py: ev.clientY,
      x: item.x,
      y: item.y,
    }
    setDrag({ id: item.id, x: item.x, y: item.y })
  }

  function onGripMove(ev: PointerEvent<HTMLSpanElement>) {
    const s = start.current
    if (!s || geometry.colWidth <= 0) return
    const item = items.find((it) => it.id === s.id)
    if (!item) return
    const dx = Math.round((ev.clientX - s.px) / geometry.colWidth)
    const dy = Math.round((ev.clientY - s.py) / (geometry.cell + geometry.gap))
    setDrag({
      id: s.id,
      x: Math.max(0, Math.min(COLUMNS - item.w, s.x + dx)),
      y: Math.max(0, s.y + dy),
    })
  }

  function onGripUp() {
    const s = start.current
    start.current = null
    if (!s) return
    setDrag((cur) => {
      if (cur && (cur.x !== s.x || cur.y !== s.y)) moveTo(cur.id, cur.x, cur.y)
      return null
    })
  }

  /*
   * Mutarea de la tastatura: cu Tab ajungi la manerul unui card, iar sagetile
   * il muta - o coloana la stanga sau la dreapta, un rand in sus sau in jos.
   * Cine nu poate trage ramanea altfel cu un constructor pe care il vede si
   * nu-l poate folosi.
   */
  function onGripKey(ev: KeyboardEvent<HTMLSpanElement>, item: BuilderItem) {
    const dx = ev.key === 'ArrowLeft' ? -1 : ev.key === 'ArrowRight' ? 1 : 0
    const dy = ev.key === 'ArrowUp' ? -1 : ev.key === 'ArrowDown' ? 1 : 0
    if (dx === 0 && dy === 0) return
    ev.preventDefault()
    moveTo(item.id, item.x + dx, item.y + dy)
  }

  /* ---- salvarea ---- */
  async function save() {
    setSaving(true)
    try {
      const out = await saveBoard(boardId, savePayload(items))
      setDirty(false)
      /* Salvat, dar fara loc: se spune, nu se lasa omul sa creada ca s-a
         tinut minte unde a pus cardurile. */
      if (out.placed === false) {
        toast(
          'The order and the sizes are saved, but not the positions: the dashboard tables need their x and y columns.',
          'Half saved',
          'warning',
        )
      }
      /* Se incarca din nou: cardurile adaugate au nevoie de cifrele lor, si
         alea se citesc pe server. */
      window.location.assign(`/app/dashboard/board/${boardId}?edit=1`)
    } catch (err) {
      setSaving(false)
      toast(err instanceof Error ? err.message : 'The layout was not saved.', 'Error', 'error')
    }
  }

  /* ---- catalogul: ce mai exista si nu e pe tablou ---- */
  const onBoard = useMemo(() => new Set(items.map((it) => it.id)), [items])
  const groups = useMemo(() => {
    const byModule = new Map<string, BuilderCard[]>()
    for (const card of catalog ?? []) {
      if (onBoard.has(card.id)) continue
      const list = byModule.get(card.module) ?? []
      list.push(card)
      byModule.set(card.module, list)
    }
    return [...byModule.entries()]
  }, [catalog, onBoard])

  function dropCustom(card: BuilderCard) {
    setAsk({
      title: 'Delete this card of yours?',
      text: 'It leaves your boards too. The measure stays in the catalogue - you can make it again.',
      ok: 'Delete',
      then: () => {
        dropCustomCard(card.id)
          .then(() => {
            setCatalog((cur) => (cur ?? []).filter((c) => c.id !== card.id))
            if (onBoard.has(card.id)) touch(items.filter((it) => it.id !== card.id))
          })
          .catch((err: unknown) => toast(err instanceof Error ? err.message : 'That did not go through.', 'Error', 'error'))
      },
    })
  }

  const rows = items.reduce((max, it) => Math.max(max, it.y + it.h), 0)
  const dragged = drag ? items.find((it) => it.id === drag.id) : undefined

  return (
    <>
      <div className="odb-editbar" id="odbEditBar" data-board={boardId} data-editing="1">
        <span className="odb-editbar-note">
          <i className="fas fa-arrows-alt" aria-hidden="true"></i> Drag a card by its handle to move it; use the buttons to
          resize.
        </span>
        {!placed && (
          <span className="odb-editbar-warn">
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Positions cannot be kept yet - the dashboard tables
            are missing their <code>x</code> and <code>y</code> columns. Apply the schema from Change Log (or run the migration)
            and come back; until then only the order and the sizes are saved.
          </span>
        )}
        <button
          type="button"
          className={`odb-btn odb-btn-main${dirty ? ' is-dirty' : ''}`}
          id="odbSave"
          disabled={saving}
          onClick={() => void save()}
        >
          {saving ? 'Saving...' : 'Save layout'}
        </button>
        <a className="odb-btn" href={`/app/dashboard/board/${boardId}`}>
          Done
        </a>
      </div>

      <div
        className="odb is-editing grid-stack grid-stack-4"
        id="odbGrid"
        ref={grid}
        style={{
          position: 'relative',
          minHeight: rows * (geometry.cell + geometry.gap) + geometry.cell,
        }}
      >
        {items.map((item) => {
          const at = drag && drag.id === item.id ? drag : item
          const style: CSSProperties = {
            position: 'absolute',
            minWidth: 0,
            maxWidth: 'none',
            left: Math.round(at.x * geometry.colWidth),
            top: at.y * (geometry.cell + geometry.gap),
            width: Math.round(item.w * geometry.colWidth),
            height: item.h * geometry.cell + (item.h - 1) * geometry.gap,
          }
          return (
            <div
              className={`odb-slot grid-stack-item odb-x${item.w} odb-y${item.h}${item.fresh ? ' is-fresh' : ''}${drag?.id === item.id ? ' ui-draggable-dragging' : ''}`}
              data-card={item.id}
              data-size={item.size}
              data-gs-x={at.x}
              data-gs-y={at.y}
              data-gs-width={item.w}
              data-gs-height={item.h}
              style={style}
              key={item.id}
            >
              <div className="grid-stack-item-content">
                <div className="odb-slot-tools">
                  <span
                    className="odb-slot-grip"
                    tabIndex={0}
                    role="button"
                    aria-label="Move this card: drag it, or use the arrow keys"
                    onPointerDown={(ev) => onGripDown(ev, item)}
                    onPointerMove={onGripMove}
                    onPointerUp={onGripUp}
                    onPointerCancel={onGripUp}
                    onKeyDown={(ev) => onGripKey(ev, item)}
                  >
                    <i className="fas fa-grip-vertical" aria-hidden="true"></i>
                  </span>
                  {Object.entries(SIZES).map(([size, dims]) => (
                    <button
                      type="button"
                      className={`odb-size${item.size === size ? ' is-on' : ''}`}
                      data-size={size}
                      data-w={dims.w}
                      data-h={dims.h}
                      title={dims.label}
                      key={size}
                      onClick={() => resize(item.id, size)}
                    >
                      {dims.label}
                    </button>
                  ))}
                  <button type="button" className="odb-slot-drop" title="Remove from this board" onClick={() => drop(item.id)}>
                    &times;
                  </button>
                </div>
                {item.fresh ? (
                  <div className="odb-card odb-fresh">
                    <div className="odb-head">
                      <span className="odb-tile">
                        <i className="fas fa-plus" aria-hidden="true"></i>
                      </span>
                      <span className="odb-title">{item.title}</span>
                    </div>
                    <div className="odb-note">Saved and drawn when you press Save layout.</div>
                  </div>
                ) : (
                  /* Invelisul e cerut de `dangerouslySetInnerHTML` (uneltele
                     sunt frati scrisi in JSX); se intinde el in locul cardului. */
                  <div
                    className="odb-slot-html"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: '1 1 auto',
                      minHeight: 0,
                      overflow: 'hidden',
                    }}
                    dangerouslySetInnerHTML={{ __html: placeLinks(item.html) }}
                  />
                )}
              </div>
            </div>
          )
        })}
        {/* Unde va cadea. O linie, nu o umbra: umbra s-ar fi confundat cu cea
            a cardului de dedesubt. */}
        {drag && dragged && (
          <div
            className="grid-stack-placeholder"
            style={{
              position: 'absolute',
              left: Math.round(drag.x * geometry.colWidth),
              top: drag.y * (geometry.cell + geometry.gap),
              width: Math.round(dragged.w * geometry.colWidth),
              height: dragged.h * geometry.cell + (dragged.h - 1) * geometry.gap,
              pointerEvents: 'none',
            }}
          >
            <div className="placeholder-content" style={{ position: 'absolute', top: 0, bottom: 0 }}></div>
          </div>
        )}
      </div>

      <div className="odb-catalog" id="odbCatalog">
        <div className="odb-catalog-head">
          <b>Add a card</b>
          <span className="odb-catalog-note">Only what you are allowed to see.</span>
          <CardMaker onMade={(card) => setCatalog((cur) => [...(cur ?? []), card])} />
        </div>
        <div className="odb-catalog-body" id="odbCatalogBody">
          {catalogFailed && <div className="odb-cat-empty">The catalogue did not answer.</div>}
          {catalog !== null && groups.length === 0 && !catalogFailed && (
            <div className="odb-cat-empty">Every card you are allowed to see is already on this board.</div>
          )}
          {groups.map(([module, list]) => (
            <div className="odb-cat-group" key={module}>
              <div className="odb-cat-module">{module}</div>
              {list.map((card) => (
                <span key={card.id}>
                  <button
                    type="button"
                    className={`odb-cat-card${card.custom ? ' odb-cat-custom' : ''}`}
                    data-card={card.id}
                    data-size={card.size}
                    title={card.question}
                    onClick={() => add(card)}
                  >
                    <i className={`fas ${card.icon}`} aria-hidden="true"></i> {card.title}
                  </button>
                  {/* Caseta proprie se poate si sterge - de aici, unde a fost
                      facuta, nu din alta parte a portalului. */}
                  {card.custom && (
                    <button
                      type="button"
                      className="odb-cat-drop"
                      data-card={card.id}
                      title="Delete this card of yours"
                      onClick={() => dropCustom(card)}
                    >
                      &times;
                    </button>
                  )}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div
        className="odb-dialog"
        id="odbBuilderDialog"
        hidden={!ask}
        onClick={(e) => e.target === e.currentTarget && setAsk(null)}
      >
        <div className="odb-dialog-box" role="dialog" aria-modal="true">
          <h4 className="odb-dialog-title">{ask?.title}</h4>
          <p className="odb-dialog-text">{ask?.text}</p>
          <div className="odb-dialog-foot">
            <button type="button" className="odb-btn" onClick={() => setAsk(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="odb-btn odb-btn-main odb-btn-danger"
              onClick={() => {
                const next = ask
                setAsk(null)
                next?.then()
              }}
            >
              {ask?.ok ?? 'OK'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * "Fa-ti o caseta" (F7): o masura din lista, un filtru, forma, marimea,
 * pragurile. Listele vin de pe server (`dashboard/measures`) o singura data,
 * cand se deschide formularul; cardul facut intra in catalog si se poate pune
 * pe tablou ca oricare altul. Ce se trimite se curata pe server
 * (`Custom::clean`) - aici doar se propune.
 */
function CardMaker({ onMade }: { onMade: (card: BuilderCard) => void }) {
  const [open, setOpen] = useState(false)
  const [measures, setMeasures] = useState<BoardMeasures | null>(null)
  const [measure, setMeasure] = useState('')
  const [goal, setGoal] = useState('flat')
  const [saving, setSaving] = useState(false)
  const form = useRef<HTMLFormElement | null>(null)

  function show() {
    setOpen(true)
    if (measures !== null) return
    boardMeasures()
      .then((out) => {
        setMeasures(out)
        pick(out.measures[0]?.key ?? '', out)
      })
      .catch(() => {
        setOpen(false)
        toast('The measures did not answer.', 'Error', 'error')
      })
  }

  const current = measures?.measures.find((m) => m.key === measure) ?? null

  /* "Good is" porneste de la ce stie masura despre ea; omul poate schimba. */
  function pick(key: string, list: BoardMeasures | null) {
    setMeasure(key)
    setGoal(list?.measures.find((m) => m.key === key)?.goal || 'flat')
  }

  async function submit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    const data: Record<string, string> = {}
    new FormData(ev.currentTarget).forEach((value, key) => {
      data[key] = String(value)
    })
    setSaving(true)
    try {
      const out = await makeCustomCard(data)
      if (out.card) {
        onMade(out.card)
        setOpen(false)
        form.current?.reset()
        toast('Your card is in the catalogue - add it to the board and save.', 'Made', 'success')
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'The card could not be made.', 'Error', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <button type="button" className="odb-btn odb-catalog-make" id="odbMake" onClick={show}>
        <i className="fas fa-magic" aria-hidden="true"></i> Make your own card
      </button>
      <form
        className="odb-maker"
        id="odbMaker"
        hidden={!open || measures === null}
        autoComplete="off"
        ref={form}
        onSubmit={(ev) => void submit(ev)}
      >
        <div className="odb-maker-grid">
          <label className="odb-maker-field odb-maker-wide">
            <span>Title</span>
            <input
              type="text"
              className="form-control"
              name="title"
              maxLength={80}
              required
              placeholder="e.g. Servers gone quiet in Cluj"
            />
          </label>
          <label className="odb-maker-field odb-maker-wide">
            <span>Measure</span>
            <select
              className="form-control"
              name="measure"
              id="odbMakerMeasure"
              value={measure}
              onChange={(e) => pick(e.target.value, measures)}
            >
              {(measures?.measures ?? []).map((m) => (
                <option value={m.key} key={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <div className="odb-maker-filters odb-maker-wide" id="odbMakerFilters">
            {(current?.filters ?? []).map((f) => (
              <label className="odb-maker-field" key={f}>
                <span>{measures?.filters[f] ?? f}</span>
                <select className="form-control" name={`filters[${f}]`} defaultValue="">
                  <option value="">Any</option>
                  {(measures?.options[f] ?? []).map((o) => (
                    <option value={o.value} key={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <label className="odb-maker-field">
            <span>Shape</span>
            <select className="form-control" name="form" id="odbMakerForm" defaultValue="">
              <option value="">Plain</option>
              {(current?.forms ?? []).map((f) => (
                <option value={f} key={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label className="odb-maker-field">
            <span>Size</span>
            <select className="form-control" name="size" id="odbMakerSize" defaultValue="1x1">
              {(measures?.sizes ?? ['1x1']).map((size) => (
                <option value={size} key={size}>
                  {size.replace('x', ' x ')}
                </option>
              ))}
            </select>
          </label>
          <label className="odb-maker-field">
            <span>Good is</span>
            <select className="form-control" name="goal" id="odbMakerGoal" value={goal} onChange={(e) => setGoal(e.target.value)}>
              <option value="flat">Just a number</option>
              <option value="low">Lower</option>
              <option value="high">Higher</option>
            </select>
          </label>
          <label className="odb-maker-field">
            <span>Watch from</span>
            <input type="number" className="form-control" name="warn" min={0} step={1} placeholder="warn" />
          </label>
          <label className="odb-maker-field">
            <span>Bad from</span>
            <input type="number" className="form-control" name="crit" min={0} step={1} placeholder="critical" />
          </label>
        </div>
        <div className="odb-maker-why" id="odbMakerWhy">
          {current?.question ?? ''}
        </div>
        <div className="odb-maker-foot">
          <span className="odb-maker-note" id="odbMakerNote">
            {measures
              ? measures.ready
                ? `Up to ${measures.limit} cards of your own.`
                : 'The table for your own cards is not installed yet - ask whoever runs the portal.'
              : ''}
          </span>
          <button type="button" className="odb-btn" id="odbMakerCancel" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button type="submit" className="odb-btn odb-btn-main" id="odbMakerSave" disabled={saving || !measures?.ready}>
            Make the card
          </button>
        </div>
      </form>
    </>
  )
}
